<template>
    <div class="modal fade show hotel-modal" aria-labelledby="checkoutModalTitle">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="d-flex justify-content-between align-items-center px-1 my-1">
                    <h1 class="mb-0">Check Out</h1>
                    <div class="d-flex align-items-center gap-1">
                        <button type="button" class="btn btn-sm btn-outline-primary" @click="$emit('onSeeDetails', reservation)">
                            See Details
                        </button>
                        <button @click="closeModal" type="button" class="btn-close" aria-label="Close"></button>
                    </div>
                </div>

                <hr class="mt-0">

                <Loader v-if="loading"/>

                <div v-if="!loading" class="modal-body">
                    <div class="row g-2 mb-2">
                        <div class="col-6">
                            <span class="detail-label">Guest</span>
                            <div class="detail-value">{{ reservation.guest_name }}</div>
                        </div>
                        <div class="col-6">
                            <span class="detail-label">Reservation No</span>
                            <div class="detail-value">{{ reservation.reservation_no }}</div>
                        </div>
                        <div class="col-6">
                            <span class="detail-label">Rooms</span>
                            <div class="detail-value">{{ roomLabel }}</div>
                        </div>
                        <div class="col-6">
                            <span class="detail-label">Check-in</span>
                            <div class="detail-value">{{ reservation.check_in }}</div>
                        </div>
                    </div>

                    <div class="row g-2 mb-2">
                        <div class="col-6">
                            <label class="detail-label mb-1">Check-out Date</label>
                            <input type="date" v-model="checkoutDate" class="form-control">
                        </div>
                    </div>

                    <div v-if="hasChecklist" class="checklist mb-2">
                        <div class="checklist-title">Checkout Checklist</div>
                        <div
                            v-for="item in checklist"
                            :key="item.note_master_id"
                            class="checklist-item"
                        >
                            <label class="checklist-row" :class="{ 'is-checked': item.checked }">
                                <input type="checkbox" v-model="item.checked" class="checklist-input">
                                <span class="tick" :class="{ 'tick-on': item.checked }">
                                    <i class="fa fa-check"></i>
                                </span>
                                <span class="checklist-label">{{ item.label }}</span>
                            </label>
                            <textarea
                                v-if="item.checked"
                                v-model="item.description"
                                class="form-control checklist-desc"
                                rows="2"
                                placeholder="Add a note (optional)"
                            ></textarea>
                        </div>
                    </div>

                    <div class="alert-note">
                        Confirm check out for this guest. The rooms will be released.
                    </div>

                    <div class="d-flex justify-content-end mt-2">
                        <button type="button" class="btn btn-outline-secondary me-1" @click="closeModal">Cancel</button>
                        <button type="button" class="btn btn-primary" :disabled="!checkoutDate || saving" @click="onConfirm">Confirm Check Out</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent';
import handleHospital from '@/services/modules/hospital';
import handleHotel from '@/services/modules/hotel/hotel';
import useDate from '@/services/utils/day';

const props = defineProps({
    reservationId: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits(['onClose', 'onCheckedOut', 'onSeeDetails']);

const $route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { notesByType } = handleHospital();
const { fetchSingleHotelReservation, checkoutHotelReservation } = handleHotel();
const { currentDate } = useDate();

const loading = ref(false);
const saving = ref(false);
const reservation = ref({});
const checklist = ref([]);
const checkoutDate = ref(currentDate());

const hasChecklist = computed(() => checklist.value.length > 0);

const roomLabel = computed(() => {
    if (!reservation.value.rooms) return '—';
    if (!reservation.value.rooms.length) return '—';
    return reservation.value.rooms.map(room => room.name).join(', ');
});

const getReservation = () => {
    loading.value = true;
    const query = `?company_id=${$route.params.companyId}`;
    fetchSingleHotelReservation(query, props.reservationId).then(res => {
        if (!res.status) {
            showError(res.message);
            return;
        }
        reservation.value = res.data;
        if (!res.data.check_out) return;
        checkoutDate.value = res.data.check_out;
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loading.value = false;
    });
};

const getChecklist = () => {
    const query = `?company_id=${$route.params.companyId}`;
    notesByType(query, 'checkout_checklist').then(res => {
        if (!res.status) return;
        checklist.value = res.data.map(item => ({
            note_master_id: item.id,
            label: item.name,
            checked: false,
            description: '',
        }));
    });
};

const buildCheckState = () => {
    return checklist.value
        .filter(item => item.checked)
        .map(item => ({
            note_master_id: item.note_master_id,
            label: item.label,
            checked: item.checked,
            description: item.description,
        }));
};

const closeModal = () => {
    emit('onClose');
};

const onConfirm = () => {
    if (!checkoutDate.value) return;
    if (!props.reservationId) return;
    if (saving.value) return;
    const payload = {
        company_id: $route.params.companyId,
        reg_status: 'released',
        check_out: checkoutDate.value,
        checkout_checklist: buildCheckState(),
    };
    saving.value = true;
    checkoutHotelReservation(payload, props.reservationId).then(res => {
        if (!res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        emit('onCheckedOut');
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        saving.value = false;
    });
};

onMounted(() => {
    getReservation();
    getChecklist();
});
</script>

<style scoped>
.hotel-modal {
    display: block;
    background: #1c1d1c33;
}
.detail-label {
    display: block;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}
.detail-value {
    font-weight: 600;
    color: #101828;
}
.alert-note {
    padding: 0.65rem 0.75rem;
    border-radius: 0.5rem;
    background: #fff8e6;
    border: 1px solid #f5e2b3;
    color: #8a6d1a;
    font-size: 0.85rem;
}
.checklist {
    border: 1px solid #eceef1;
    border-radius: 0.5rem;
    padding: 0.5rem 0.65rem;
}
.checklist-title {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 0.4rem;
}
.checklist-item {
    padding: 0.15rem 0;
}
.checklist-row {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    padding: 0.3rem 0;
    cursor: pointer;
    margin-bottom: 0;
}
.checklist-desc {
    margin: 0.15rem 0 0.35rem 1.75rem;
    width: calc(100% - 1.75rem);
    font-size: 0.82rem;
    resize: vertical;
}
.checklist-input {
    display: none;
}
.tick {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 6px;
    border: 1.5px solid #d0d5dd;
    display: flex;
    align-items: center;
    justify-content: center;
    color: transparent;
    font-size: 0.7rem;
    transition: all 0.15s ease;
}
.tick-on {
    background: #1fa363;
    border-color: #1fa363;
    color: #ffffff;
}
.checklist-label {
    font-size: 0.85rem;
    color: #475467;
}
.checklist-row.is-checked .checklist-label {
    color: #101828;
    font-weight: 600;
}
</style>
